import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, Heart, Stethoscope } from 'lucide-react';
import Section, { SectionTitle, SectionSubtitle } from '../components/ui/Section';
import HeroSection from '../components/ui/HeroSection';
import HeroCard from '../components/ui/HeroCard';
import Button from '../components/ui/Button';

const ApplicationsPage = () => {
  return (
    <div id="top" className="font-sans text-black">
      {/* Hero Section */}
      <HeroSection
        title="Real-World Applications"
        description="Senti's Large Biosignal Model is transforming healthcare across multiple critical areas, enabling earlier detection and more effective management of various conditions."
        buttonText="View Use Cases"
        buttonLink="#applications-grid"
        buttonVariant="primary"
        buttonScrollTo="applications-grid"
        centered={false}
        paddingY="py-32"
      >
        <HeroCard 
          icon={<Activity className="h-20 w-20 text-primary" />}
          title="Transforming Patient Care"
          description="Our AI technology enables proactive healthcare across multiple conditions by detecting patterns before symptoms emerge."
        />
      </HeroSection>

      {/* Applications Grid */}
      <Section id="applications-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Respiratory Disease Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 flex flex-col">
              <div className="bg-primary h-3"></div>
              <div className="p-6 flex flex-col h-full">
                <div>
                  <h3 className="text-2xl font-bold text-primary h-16 mb-4">Respiratory Disease Management</h3>
                  <p className="text-gray-700 min-h-[80px]">
                    Our respiratory wearable device monitors breath and heart sounds, using the LBM to detect early signs of flare-ups with over 90% sensitivity before symptoms appear.
                  </p>
                </div>
                <div className="border-t border-gray-200 pt-4 mt-4 w-full">
                  <p className="text-primary font-semibold">Impact:</p>
                  <p className="text-gray-700">Potential to save billions in healthcare costs and dramatically improve outcomes for 136M respiratory disease patients.</p>
                </div>
              </div>
            </div>

            {/* Rheumatoid Arthritis Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 flex flex-col">
              <div className="bg-secondary h-3"></div>
              <div className="p-6 flex flex-col h-full">
                <div>
                  <h3 className="text-2xl font-bold text-secondary h-16 mb-4">Rheumatoid Arthritis Diagnosis</h3>
                  <p className="text-gray-700 min-h-[80px]">
                    The Arthritect system accelerates diagnosis of rheumatoid arthritis from months or years down to weeks through bioimpedance signal analysis.
                  </p>
                </div>
                <div className="border-t border-gray-200 pt-4 mt-4 w-full">
                  <p className="text-secondary font-semibold">Impact:</p>
                  <p className="text-gray-700">Could unlock over £1.5 billion in direct healthcare savings annually through earlier detection and treatment.</p>
                </div>
              </div>
            </div>

            {/* Fetal Wellbeing Card */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 flex flex-col">
              <div className="bg-accent h-3"></div>
              <div className="p-6 flex flex-col h-full">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 h-16 mb-4">Fetal Wellbeing Monitoring</h3>
                  <p className="text-gray-700 min-h-[80px]">
                    Bitbaby, powered by our LBM, monitors fetal movements to provide a reliable measure of fetal health and wellbeing.
                  </p>
                </div>
                <div className="border-t border-gray-200 pt-4 mt-4 w-full">
                  <p className="text-gray-800 font-semibold">Impact:</p>
                  <p className="text-gray-700">Helping to reduce stillbirths among the 12M pregnancies in high-income countries each year.</p>
                </div>
              </div>
            </div>
        </div>
      </Section>

      {/* Additional Applications Section */}
      <Section background="light" id="future-applications">
        <div className="max-w-4xl mx-auto">
            <SectionTitle>Future Applications</SectionTitle>
            <p className="text-lg text-gray-700 mb-8 text-center">
              The potential applications of our Large Biosignal Model extend far beyond our current implementations.
              As we continue to develop and enhance our technology, we're exploring these promising areas:
            </p>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow transition-transform hover:scale-105 animate-fade-in">
                <h3 className="text-xl font-bold text-primary mb-2">Neurological Disorder Detection</h3>
                <p className="text-gray-700">Early detection of conditions like Parkinson's through subtle movement pattern analysis.</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow transition-transform hover:scale-105 animate-fade-in animation-delay-2000">
                <h3 className="text-xl font-bold text-primary mb-2">Cardiovascular Risk Assessment</h3>
                <p className="text-gray-700">Continuous monitoring of heart signals to predict cardiovascular events before they occur.</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow transition-transform hover:scale-105 animate-fade-in animation-delay-4000">
                <h3 className="text-xl font-bold text-primary mb-2">Sleep Disorder Diagnosis</h3>
                <p className="text-gray-700">Accurate analysis of sleep patterns for diagnosing conditions like sleep apnea and insomnia.</p>
              </div>
            </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="secondary" paddingY="py-16">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">Interested in partnering with Senti?</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-white">
            We're looking for innovative partners to help bring the power of our Large Biosignal Model to new medical applications.
          </p>
          <Button 
            to="/contact" 
            className="inline-block bg-white text-secondary font-bold py-3 px-8 rounded-md hover:bg-accent transition-colors"
            scrollTo="top"
          >
            Contact Us
          </Button>
        </div>
      </Section>
    </div>
  );
};

export default ApplicationsPage;
